import { ChangeDetectionStrategy, Component, inject, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatCardModule } from "@angular/material/card";
import { MatTableModule } from "@angular/material/table";
import { MatIconModule } from "@angular/material/icon";
import { MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";

import { IPropertyDamageAssessmentStructure } from "../../../interfaces/i-property-damage-assessment-structure";
import { StructureCardComponent } from "../../property-overview/structure-card/structure-card.component";
import { DamageStyleService } from "../../../services/internal-services/damage-style.service";
import { DocumentService } from "../../../services/internal-services/document.service";

@Component({
  selector: "lib-damage-structure-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./damage-structure-card.component.html",
  styleUrls: ["./damage-structure-card.component.scss"],
  imports: [
    CommonModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatCardModule,
    MatTableModule,
    MatIconModule,
    MatIconButton,
    MatTooltip,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class DamageStructureCardComponent extends StructureCardComponent {
  /** Post-catastrophe damage data (FEMA score, roof condition percentages). */
  @Input() damageStructure: IPropertyDamageAssessmentStructure;

  // Inherited from StructureCardComponent:
  // @Input() structure: IPropertyStructure  ← pre-damage structure
  // @Input() parcelWkt, index, measurementSystem, expanded
  // getRoofScoreColor, getRoofScoreLabel, getDsColor, formatArea, formatLength
  // formatBuildingType, getDefensibleSpaceBands, onZoomClick

  private damageStyleService = inject(DamageStyleService);

  /** Extracts the numeric FEMA level (0–6) from the raw classification string. Null maps to 0. */
  getFemaLevel(fema: string | null): number | null {
    if (fema == null) return 0;
    const match = fema.match(/FEMA (\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  /** Returns the severity label for the given FEMA classification string. */
  getFemaLabel(fema: string | null): string {
    const level = this.getFemaLevel(fema);
    if (level == null) return fema ?? "No Damage";
    return this.damageStyleService.getFemaMap()[level]?.label ?? fema ?? "No Damage";
  }

  /** Returns the hex color corresponding to the FEMA classification level, sourced from the API style. */
  getFemaColor(fema: string | null): string {
    const level = this.getFemaLevel(fema);
    if (level == null) return "#9e9e9e";
    return this.damageStyleService.getFemaMap()[level]?.color ?? "#9e9e9e";
  }

  /** Copies the given WKT value to the clipboard. */
  copyToClipboard(value: string): void {
    DocumentService.copyTextToClipboard(value);
  }
}
